document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-variants]')) return;
  const variants = JSON.parse(select.dataset.variants);
  const chosen = variants.find((variant) => String(variant.id) === select.value);
  const panel = select.closest('.tl-product');
  if (!chosen || !panel) return;
  const format = (value) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency', currency: select.dataset.currency,
  }).format(value / 100);
  panel.querySelector('[data-price]').textContent = format(chosen.price);
  const compare = panel.querySelector('[data-compare]');
  compare.hidden = !(chosen.compare_at_price > chosen.price);
  if (!compare.hidden) compare.textContent = format(chosen.compare_at_price);
  const add = panel.querySelector('[data-add-button]');
  add.disabled = !chosen.available;
  add.textContent = chosen.available ? select.dataset.add : select.dataset.soldOut;
  panel.querySelector('[data-stock]').textContent = chosen.available ? select.dataset.available : select.dataset.soldOut;
  const quantity = panel.querySelector('input[name="quantity"]');
  if (quantity) {
    const rule = chosen.quantity_rule || { min: 1, increment: 1 };
    quantity.min = String(rule.min);
    quantity.step = String(rule.increment);
    if (rule.max == null) quantity.removeAttribute('max');
    else quantity.max = String(rule.max);
    let value = Math.max(rule.min, Number(quantity.value) || rule.min);
    if (rule.max != null) value = Math.min(rule.max, value);
    quantity.value = String(rule.min + Math.floor((value - rule.min) / rule.increment) * rule.increment);
  }
  const image = panel.querySelector('.tl-product-image');
  if (image && chosen.featured_image?.src && !panel.querySelector('tl-product-gallery-component')) {
    image.src = chosen.featured_image.src;
    image.removeAttribute('srcset');
  }
  syncGalleryForVariant(panel, chosen);
  const url = new URL(location.href);
  url.searchParams.set('variant', chosen.id);
  history.replaceState({}, '', url);
});
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const link = event.target.closest('[data-gallery-image]');
  if (!link) return;
  const image = link.closest('.tl-gallery')?.querySelector('.tl-product-image');
  if (image) {
    event.preventDefault();
    setGalleryMedia(link.closest('.tl-gallery'), link);
  }
});

// Enhance the native variant select; it remains the sole submitted variant ID.
function variantOptions(variant) {
  return variant.options.map((option) => String(option?.name ?? option));
}

function syncVariantOptions(select) {
  if (select.hasAttribute('data-server-variants')) return;
  const panel = select.closest('.tl-product');
  const picker = panel?.querySelector('[data-option-picker]');
  if (!picker) return;
  const variants = JSON.parse(select.dataset.variants);
  const chosen = variants.find((variant) => String(variant.id) === select.value);
  if (!chosen) return;
  const selected = variantOptions(chosen);
  for (const group of picker.querySelectorAll('[data-option-position]')) {
    const position = Number(group.dataset.optionPosition);
    group.querySelector('[data-selected-option]').textContent = selected[position];
    for (const button of group.querySelectorAll('button')) {
      button.setAttribute('aria-pressed', String(button.dataset.optionValue === selected[position]));
    }
    const control = group.querySelector('select');
    if (control) {
      control.value = selected[position];
      for (const option of control.options) {
        option.disabled = !variants.some((variant) => {
          const values = variantOptions(variant);
          return values[position] === option.value && selected.slice(0, position).every((value, index) => values[index] === value);
        });
      }
    }
  }
}

function enhanceVariantOptions(root = document) {
  for (const select of root.querySelectorAll('[data-variants][data-option-names]')) {
    if (select.dataset.optionsEnhanced || select.hasAttribute('data-server-variants')) continue;
    const variants = JSON.parse(select.dataset.variants);
    const names = JSON.parse(select.dataset.optionNames);
    if (!variants.length || !names.length || !variants.every((variant) => Array.isArray(variant.options))) continue;
    const chosen = variants.find((variant) => String(variant.id) === select.value);
    if (!chosen) continue;
    const picker = document.createElement('div');
    picker.className = 'tl-option-picker';
    picker.dataset.optionPicker = '';
    names.forEach((name, position) => {
      const values = [...new Set(variants.map((variant) => variantOptions(variant)[position]))];
      const group = document.createElement('fieldset');
      group.dataset.optionPosition = String(position);
      const legend = document.createElement('legend');
      legend.id = `${select.id}-option-${position}`;
      legend.append(document.createTextNode(`${name}: `));
      const current = document.createElement('strong');
      current.dataset.selectedOption = '';
      legend.append(current);
      group.append(legend);
      if (position === 0 && values.length <= 5) {
        const buttons = document.createElement('div');
        buttons.className = 'tl-option-buttons';
        for (const value of values) {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.optionValue = value;
          button.textContent = value;
          buttons.append(button);
        }
        group.append(buttons);
      } else {
        const control = document.createElement('select');
        control.setAttribute('aria-labelledby', legend.id);
        for (const value of values) control.add(new Option(value, value));
        group.append(control);
      }
      picker.append(group);
    });
    const choose = (control, value) => {
      const position = Number(control.closest('[data-option-position]').dataset.optionPosition);
      const current = variants.find((variant) => String(variant.id) === select.value);
      const desired = variantOptions(current);
      desired[position] = value;
      const candidates = variants.filter((variant) => variantOptions(variant)[position] === value);
      const next = candidates.find((variant) => variantOptions(variant).every((option, index) => option === desired[index]))
        || candidates.find((variant) => variant.available) || candidates[0];
      if (!next) return;
      select.value = String(next.id);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    };
    picker.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('button[data-option-value]');
      if (button) choose(button, button.dataset.optionValue);
    });
    picker.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement) choose(event.target, event.target.value);
    });
    const label = select.closest('.tl-product').querySelector(`label[for="${select.id}"]`);
    select.before(picker);
    select.dataset.optionsEnhanced = 'true';
    syncVariantOptions(select);
    select.hidden = true;
    if (label) label.hidden = true;
  }
}

enhanceVariantOptions();
document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLSelectElement && event.target.matches('[data-variants]')) syncVariantOptions(event.target);
});
document.addEventListener('shopify:section:load', (event) => enhanceVariantOptions(event.target));

function galleryPath(url) {
  return new URL(url, location.href).pathname;
}

function setGalleryMedia(gallery, link) {
  const image = gallery.querySelector('.tl-product-image');
  const zoom = gallery.querySelector('[data-media-zoom]');
  image.src = link.dataset.galleryImage;
  image.removeAttribute('srcset');
  image.alt = link.dataset.galleryAlt;
  zoom.href = link.href;
  const links = [...gallery.querySelectorAll('[data-gallery-image]')];
  links.forEach((item) => {
    if (item === link) item.setAttribute('aria-current', 'true');
    else item.removeAttribute('aria-current');
  });
  gallery.querySelectorAll('[data-gallery-status]').forEach((status) => {
    status.textContent = `${links.indexOf(link) + 1} / ${links.length}`;
  });
  const dialog = gallery.querySelector('dialog');
  if (dialog.open) updateGalleryZoom(gallery);
}

function syncGalleryForVariant(panel, variant) {
  const gallery = panel.querySelector('.tl-gallery');
  if (gallery?.localName === 'tl-product-gallery-component') {
    gallery.selectVariant?.(variant);
    return;
  }
  if (!gallery || !variant.featured_image?.src) return;
  const links = [...gallery.querySelectorAll('[data-gallery-image]')];
  const link = links.find((item) => item.dataset.galleryId === String(variant.featured_image.id))
    || links.find((item) => galleryPath(item.dataset.galleryImage) === galleryPath(variant.featured_image.src));
  if (link) setGalleryMedia(gallery, link);
  else gallery.querySelector('[data-media-zoom]').href = variant.featured_image.src;
}

function updateGalleryZoom(gallery) {
  const main = gallery.querySelector('.tl-product-image');
  const image = gallery.querySelector('[data-zoom-image]');
  image.src = gallery.querySelector('[data-media-zoom]').href;
  image.alt = main.alt;
}

function enhanceGallery(root = document) {
  for (const gallery of root.querySelectorAll('.tl-gallery')) {
    if (gallery.localName === 'tl-product-gallery-component' || gallery.dataset.galleryEnhanced || !gallery.querySelector('dialog')) continue;
    const links = [...gallery.querySelectorAll('[data-gallery-image]')];
    if (!links.length) continue;
    const current = gallery.querySelector('.tl-product-image');
    const initial = links.find((link) => galleryPath(link.dataset.galleryImage) === galleryPath(current.src)) || links[0];
    setGalleryMedia(gallery, initial);
    gallery.querySelectorAll('[data-gallery-controls]').forEach((controls) => controls.hidden = false);
    const dialog = gallery.querySelector('dialog');
    let opener;
    gallery.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const zoom = event.target.closest('[data-media-zoom]');
      if (zoom && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        updateGalleryZoom(gallery);
        opener = zoom;
        dialog.showModal();
        return;
      }
      const step = event.target.closest('[data-gallery-step]');
      if (step) {
        const active = gallery.querySelector('[data-gallery-image][aria-current="true"]');
        const index = (links.indexOf(active) + Number(step.dataset.galleryStep) + links.length) % links.length;
        setGalleryMedia(gallery, links[index]);
      }
      if (event.target === dialog) {
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }
    });
    dialog.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const active = gallery.querySelector('[data-gallery-image][aria-current="true"]');
      const index = (links.indexOf(active) + (event.key === 'ArrowRight' ? 1 : -1) + links.length) % links.length;
      setGalleryMedia(gallery, links[index]);
    });
    dialog.addEventListener('close', () => opener?.focus());
    gallery.dataset.galleryEnhanced = 'true';
  }
}

enhanceGallery();
document.addEventListener('shopify:section:load', (event) => enhanceGallery(event.target));

function enhanceFeaturedCategories(root = document) {
  for (const list of root.querySelectorAll('[data-featured-tabs]')) {
    if (list.dataset.tabsEnhanced) continue;
    const section = list.closest('.tl-featured');
    const tabs = [...list.querySelectorAll('[data-featured-tab]')];
    const panels = tabs.map((tab) => section.querySelector(`#${CSS.escape(tab.dataset.featuredTab)}`));
    if (!tabs.length || panels.some((panel) => !panel)) continue;
    list.setAttribute('role', 'tablist');
    const activate = (index, focus = false) => {
      tabs.forEach((tab, position) => {
        const active = position === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        panels[position].hidden = !active;
      });
      if (focus) {
        tabs[index].focus({ preventScroll: true });
        tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      }
    };
    tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panels[index].id);
      panels[index].setAttribute('role', 'tabpanel');
      panels[index].setAttribute('aria-labelledby', tab.id);
      tab.addEventListener('click', (event) => {
        if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
        event.preventDefault();
        activate(index);
      });
      tab.addEventListener('keydown', (event) => {
        let next;
        const direction = getComputedStyle(list).direction === 'rtl' ? -1 : 1;
        if (event.key === 'ArrowRight') next = (index + direction + tabs.length) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (index - direction + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else if (event.key === ' ') next = index;
        else return;
        event.preventDefault();
        activate(next, true);
      });
    });
    activate(0);
    list.dataset.tabsEnhanced = 'true';
  }
}
enhanceFeaturedCategories();
document.addEventListener('shopify:section:load', (event) => enhanceFeaturedCategories(event.target));
